import { loyaltyApi } from "@/modules/loyalty/presentation/api/client";
import type {
  PromotionCreatePayload,
  PromotionScopeType,
  PromotionType,
} from "@/modules/promotions/domain/types";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteMultiCombobox } from "@/shared/ui/InfiniteMultiCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { FormRoot, FormInput, FormSelect, FormNumber, FormPrice, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";


import {
  ArrowLeftIcon,
  ImageIcon,
  InfoIcon,
  NetworkIcon,
  SettingsIcon,
  Trash2Icon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import {
  PROMOTION_IMAGE_KEY_PREFIX,
  PromotionImageUploadField,
} from "./PromotionImageUploadField";

const PROMOTION_TYPES: { value: PromotionType; label: string }[] = [
  { value: "PERCENT", label: "ສ່ວນຫຼຸດເປີ້ cent (%)" },
  { value: "FIXED", label: "ສ່ວນຫຼຸດคงที่ (LAK)" },
  { value: "BXGY", label: "ຊື້ X ແບບ Y" },
  { value: "SET", label: "ຊຸດລາຄາພິເສດ" },
];

const SCOPE_TYPES: { value: PromotionScopeType; label: string }[] = [
  { value: "BRANCH", label: "ສາຂາ" },
  { value: "PRODUCT", label: "ສິນຄ້າ" },
  // { value: "CATEGORY", label: "ຫມວດສິນຄ້າ" },
];

const ScopeRowSchema = z.object({
  scopeType: z.enum(["BRANCH", "PRODUCT", "CATEGORY"]),
  scopeIds: z.array(z.string()).min(1, "ກະລຸນາເລືອກຢ່າງນ້ອຍ 1 ລາຍການ"),
});

const PromotionFormSchema = z
  .object({
    name: z.string().min(1, "ກະລຸນາໃສ່ຊື່ໂປຣໂມຊັນ"),
    imageUrl: z.string().nullable().optional(),
    type: z.enum(["PERCENT", "FIXED", "BXGY", "SET"]),
    startAt: z.string().min(1, "ກະລຸນາໃສ່ວັນທີເລີ່ມ"),
    endAt: z.string().min(1, "ກະລຸນາໃສ່ວັນທີສິ້ນສຸດ"),
    minOrderTotal: z.number().int().min(0).nullable(),
    percentValue: z.number().int().min(1).max(100).nullable(),
    fixedAmount: z.number().int().min(0).nullable(),
    globalLimit: z.number().int().min(0).nullable(),
    perCustomerLimit: z.number().int().min(0).nullable(),
    priority: z.number().int(),
    showOnOpen: z.boolean(),
    repeatOnOpen: z.boolean(),
    sendPush: z.boolean(),
    isActive: z.boolean(),
    scopes: z.array(ScopeRowSchema),
  })
  .refine((d) => new Date(d.startAt) < new Date(d.endAt), {
    message: "ວັນທີເລີ່ມຕ້ອງກ່ອນວັນທີສິ້ນສຸດ",
    path: ["endAt"],
  });

export type PromotionFormValues = z.infer<typeof PromotionFormSchema>;

type PromotionFormProps = {
  defaultValues?: Partial<PromotionFormValues>;
  /** เปลี่ยนเมื่อโหลดข้อมูลจาก server ใหม่ (เช่น promotion.updatedAt) */
  syncKey?: string;
  onSubmit: (values: PromotionCreatePayload) => void | Promise<void>;
  submitting?: boolean;
  onCancel: () => void;
  submitLabel: string;
};

function buildDefaultValues(
  partial?: Partial<PromotionFormValues>,
): PromotionFormValues {
  return {
    name: "",
    imageUrl: null,
    type: "PERCENT",
    startAt: "",
    endAt: "",
    minOrderTotal: null,
    percentValue: null,
    fixedAmount: null,
    globalLimit: null,
    perCustomerLimit: 1,
    priority: 10,
    showOnOpen: false,
    repeatOnOpen: true,
    sendPush: false,
    isActive: true,
    scopes: [],
    ...partial,
  };
}

function toDatetimeLocal(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${day}T${h}:${min}`;
}

function fromDatetimeLocal(local: string): string {
  if (!local) return "";
  return new Date(local).toISOString();
}

export function PromotionForm({
  defaultValues,
  syncKey,
  onSubmit,
  submitting,
  onCancel,
  submitLabel,
}: PromotionFormProps) {
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const methods = useForm<PromotionFormValues>({
    resolver: zodResolver(PromotionFormSchema),
    defaultValues: buildDefaultValues(defaultValues),
  });

  useEffect(() => {
    if (!syncKey) return;
    methods.reset(buildDefaultValues(defaultValues));
    setPendingImageFile(null);
    // sync เฉพาะเมื่อ server data เปลี่ยน (updatedAt) ไม่ reset ทุก render
    // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultValues ถูกสร้างใหม่ทุก render
  }, [syncKey]);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "scopes",
  });

  const handleSubmit = async (values: PromotionFormValues) => {
    let imageUrl = values.imageUrl?.trim() || null;
    if (pendingImageFile) {
      imageUrl = await uploadImageToKey(
        PROMOTION_IMAGE_KEY_PREFIX,
        pendingImageFile,
      );
      methods.setValue("imageUrl", imageUrl);
      setPendingImageFile(null);
    }

    await onSubmit({
      name: values.name.trim(),
      imageUrl,
      type: values.type,
      startAt: fromDatetimeLocal(values.startAt),
      endAt: fromDatetimeLocal(values.endAt),
      minOrderTotal: values.minOrderTotal ?? null,
      percentValue: values.percentValue ?? null,
      fixedAmount: values.fixedAmount ?? null,
      perCustomerLimit: values.perCustomerLimit ?? null,
      globalLimit: values.globalLimit ?? null,
      priority: values.priority,
      showOnOpen: values.showOnOpen,
      repeatOnOpen: values.repeatOnOpen,
      sendPush: values.sendPush,
      isActive: values.isActive,
      scopes: values.scopes.flatMap((s) =>
        s.scopeIds.map((scopeId) => ({ scopeType: s.scopeType, scopeId })),
      ),
    });
  };

  return (
    <FormRoot<PromotionFormValues> methods={methods} onSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* Promotion Image */}
        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <ImageIcon className="size-5 text-muted-foreground" />
              <h3 className="font-semibold text-base">ຮູບໂປຣໂມຊັນ</h3>
            </div>
            <p className="text-muted-foreground text-sm">
              ແສດງໃນແອັບລູກຄ້າ (carousel ຫນ້າຫຼັກ) — ອັດຕາສ່ວນ 4:3
            </p>
            <RHF.Controller
              name="imageUrl"
              control={methods.control}
              render={({ field }) => (
                <PromotionImageUploadField
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  pendingFile={pendingImageFile}
                  onFileSelect={setPendingImageFile}
                  disabled={submitting}
                />
              )}
            />
          </CardContent>
        </Card>

        {/* General Information */}
        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <InfoIcon className="size-5 text-muted-foreground" />
              <h3 className="font-semibold text-base">ຂໍ້ມູນທົ່ວໄປ</h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormInput
                name="name"
                label="ຊື່ໂປຣໂມຊັນ"
                placeholder="ຕົວຢ່າງ: ສ່ວນຫຼຸດລະດູຮ້ອນ 2024"
                requiredMark
              />
              <FormSelect
                label="ປະເພດ"
                options={PROMOTION_TYPES}
                placeholder="ເລືອກປະເພດ"
                name="type"
              />
              {methods.watch("type") === "PERCENT" && (
                <FormNumber
                  name="percentValue"
                  label="ເປີເຊັນສ່ວນຫຼຸດ (%)"
                  placeholder="1-100"
                  min={1}
                  max={100}
                />
              )}
              {methods.watch("type") === "FIXED" && (
                <FormPrice
                  name="fixedAmount"
                  label="ຈຳນວນຫຼຸດ (LAK)"
                  currencySymbol="K"
                  placeholder="0"
                />
              )}
              <div className="space-y-2">
                <label
                  htmlFor="promotion-start-at"
                  className="font-medium text-sm"
                >
                  ວັນທີເລີ່ມ - ເວລາ
                </label>
                <RHF.Controller
                  name="startAt"
                  control={methods.control}
                  render={({ field }) => (
                    <input
                      id="promotion-start-at"
                      type="datetime-local"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                      value={field.value ? toDatetimeLocal(field.value) : ""}
                      onChange={(e) =>
                        field.onChange(fromDatetimeLocal(e.target.value))
                      }
                    />
                  )}
                />
                {methods.formState.errors.startAt && (
                  <p className="text-destructive text-xs">
                    {methods.formState.errors.startAt.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="promotion-end-at"
                  className="font-medium text-sm"
                >
                  ວັນທີສິ້ນສຸດ - ເວລາ
                </label>
                <RHF.Controller
                  name="endAt"
                  control={methods.control}
                  render={({ field }) => (
                    <input
                      id="promotion-end-at"
                      type="datetime-local"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                      value={field.value ? toDatetimeLocal(field.value) : ""}
                      onChange={(e) =>
                        field.onChange(fromDatetimeLocal(e.target.value))
                      }
                    />
                  )}
                />
                {methods.formState.errors.endAt && (
                  <p className="text-destructive text-xs">
                    {methods.formState.errors.endAt.message}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">ເປີດໃຊ້ງານ</span>
                <RHF.Controller
                  name="isActive"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  ແຈ້ງເຕືອນຕອນເປີດແອັບ
                </span>
                <RHF.Controller
                  name="showOnOpen"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
              {methods.watch("showOnOpen") ? (
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">
                    ສະແດງຊ້ຳທຸກຄັ້ງທີ່ເປີດແອັບ
                  </span>
                  <RHF.Controller
                    name="repeatOnOpen"
                    control={methods.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              ) : null}
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">ສົ່ງ push (FCM)</span>
                <RHF.Controller
                  name="sendPush"
                  control={methods.control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Push ຄັ້ງດຽວຕອນເລີ່ມແຄມເປນ — ບໍ່ຊ້ຳຕາມ popup repeat
            </p>
          </CardContent>
        </Card>

        {/* Usage & Constraints */}
        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <SettingsIcon className="size-5 text-muted-foreground" />
              <h3 className="font-semibold text-base">ການນຳໃຊ້ ແລະ ຂໍ້ຈຳກັດ</h3>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <FormPrice
                name="minOrderTotal"
                label="ຍອດຂັ້ນຕ່ຳ (LAK)"
                currencySymbol="K"
                placeholder="0"
              />
              <FormNumber
                name="globalLimit"
                label="ຈຳກັດທົ່ວລະບົບ"
                placeholder="ບໍ່ຈຳກັດ"
              />
              <FormNumber
                name="perCustomerLimit"
                label="ຕໍ່ລູກຄ້າສູງສຸດ"
                placeholder="1"
              />
              <FormNumber
                name="priority"
                label="ລຳດັບຄວາມສຳຄັນ"
                placeholder="10"
                hint="ຕົວເລກນ້ອຍ = ສຳຄັນກ່ອນ"
              />
            </div>
          </CardContent>
        </Card>

        {/* Eligibility Scopes */}
        <Card className="rounded-xl border">
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <NetworkIcon className="size-5 text-muted-foreground" />
                <h3 className="font-semibold text-base">ຂອບເຂດການສົມຄວນ</h3>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => append({ scopeType: "BRANCH", scopeIds: [] })}
              >
                + ເພີ່ມກົດ
              </Button>
            </div>
            {fields.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີກົດ ກົດໃຊ້ທຸກສາຂາ/ທຸກສິນຄ້າຖ້າບໍ່ເພີ່ມ
              </p>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-12 gap-2 font-medium text-muted-foreground text-xs">
                  <div className="col-span-3">ປະເພດ</div>
                  <div className="col-span-7">ເປົ້າໝາຍ</div>
                  <div className="col-span-2" />
                </div>
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-12 items-start gap-2"
                  >
                    <div className="col-span-3">
                      <RHF.Controller
                        name={`scopes.${index}.scopeType`}
                        control={methods.control}
                        render={({ field: f }) => (
                          <SimpleSelect
                            options={SCOPE_TYPES}
                            value={f.value}
                            onValueChange={(v) => {
                              f.onChange(v);
                              methods.setValue(`scopes.${index}.scopeIds`, []);
                            }}
                            placeholder="ເລືອກ"
                            className="w-full"
                          />
                        )}
                      />
                    </div>
                    <div className="col-span-7">
                      <RHF.Controller
                        name={`scopes.${index}.scopeIds`}
                        control={methods.control}
                        render={({ field: f }) => {
                          const scopeType = methods.watch(
                            `scopes.${index}.scopeType`,
                          );
                          const values = Array.isArray(f.value) ? f.value : [];
                          if (scopeType === "CATEGORY") {
                            return (
                              <input
                                type="text"
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                placeholder="ລະຫັດຫມວດ (ຖ້າມີ)"
                                value={values[0] ?? ""}
                                onChange={(e) =>
                                  f.onChange(
                                    e.target.value ? [e.target.value] : [],
                                  )
                                }
                              />
                            );
                          }
                          if (scopeType === "BRANCH") {
                            return (
                              <InfiniteMultiCombobox<{
                                id: string;
                                name: string;
                              }>
                                queryKey={["branches", "lookup"]}
                                queryFn={(args) =>
                                  fetchLookupForInfinite(
                                    `${config.apiUrl}/branches/lookup`,
                                    args,
                                  )
                                }
                                preloadQueryFn={(ids) =>
                                  ids.length === 0
                                    ? Promise.resolve([])
                                    : Promise.all(
                                        ids.map((id) =>
                                          hydrateLookupItem(
                                            `${config.apiUrl}/branches/lookup`,
                                            id,
                                          ),
                                        ),
                                      ).then((items) =>
                                        items.filter(
                                          (
                                            x,
                                          ): x is {
                                            id: string;
                                            name: string;
                                          } => x != null,
                                        ),
                                      )
                                }
                                getLabel={(item) => item.name}
                                getValue={(item) => item.id}
                                values={values}
                                onValuesChange={f.onChange}
                                placeholder="ເລືອກສາຂາ..."
                                className="w-full"
                                clearable
                              />
                            );
                          }
                          return (
                            <InfiniteMultiCombobox<{
                              id: string;
                              displayName: string;
                            }>
                              queryKey={[
                                "loyalty",
                                "rewards",
                                "variant-options",
                              ]}
                              queryFn={({ search, pageParam }) =>
                                loyaltyApi.listVariantOptionsLookup({
                                  search: search || undefined,
                                  page: pageParam,
                                  limit: 20,
                                })
                              }
                              preloadQueryFn={(ids) =>
                                ids.length === 0
                                  ? Promise.resolve([])
                                  : Promise.all(
                                      ids.map((id) =>
                                        loyaltyApi.getVariantOption(id),
                                      ),
                                    ).then((items) =>
                                      items.filter(
                                        (
                                          x,
                                        ): x is {
                                          id: string;
                                          displayName: string;
                                        } => x != null,
                                      ),
                                    )
                              }
                              getLabel={(item) => item.displayName}
                              getValue={(item) => item.id}
                              values={values}
                              onValuesChange={f.onChange}
                              placeholder="ເລືອກສິນຄ້າ..."
                              className="w-full"
                              clearable
                            />
                          );
                        }}
                      />
                      {methods.formState.errors.scopes?.[index]?.scopeIds && (
                        <p className="mt-1 text-destructive text-xs">
                          {
                            methods.formState.errors.scopes[index]?.scopeIds
                              ?.message
                          }
                        </p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(index)}
                        aria-label="ລຶບ"
                      >
                        <Trash2Icon className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "ກຳລັງບັນທຶກ..." : submitLabel}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          <ArrowLeftIcon className="size-4" />
          ຍົກເລີກ
        </Button>
      </div>
    </FormRoot>
  );
}
